import { Colors, Lightning, Router } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { CategoryList } from "../../components/data-display/CategoryList/CategoryList";
import { MediaList } from "../../components/data-display/MediaList/MediaList";
import theme from "../../lib/theme";
import { CategoryModel, MediaModel } from "../../lib/models";
import { MENU_WIDTH, PAGES } from "../../lib/utils";
import configs from "../../lib/configs";

interface FavoriteTemplateSpec extends Lightning.Component.TemplateSpec {
    Empty: {
        Text: typeof TranslatableText;
    };
    List: typeof CategoryList;
    GridList: typeof MediaList;
}

interface FavoriteTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

export class Favorite
    extends Lightning.Component<FavoriteTemplateSpec, FavoriteTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<FavoriteTemplateSpec>
{
    readonly List = this.getByRef("List")!;
    readonly GridList = this.getByRef("GridList")!;
    readonly Empty = this.getByRef("Empty")!;

    _isGridView = ((configs as any).app?.layout?.bookmarks?.view ?? configs.layout?.bookmarks?.view) === "grid";

    static override _template(): Lightning.Component.Template<FavoriteTemplateSpec> {
        const isGridView =
            ((configs as any).app?.layout?.bookmarks?.view ?? configs.layout?.bookmarks?.view) === "grid";

        return {
            Empty: {
                alpha: 0,
                x: MENU_WIDTH,
                w: theme.layout.w - MENU_WIDTH,
                h: theme.layout.h,
                Text: {
                    w: 360,
                    mount: 0.5,
                    x: (w) => w / 2,
                    y: (h) => h / 2,
                    type: TranslatableText,
                    key: "bookmarks.empty",
                    text: {
                        textAlign: "center",
                        fontSize: 32,
                        lineHeight: 42,
                        textColor: Colors(theme.colors.text.secondary).get()
                    }
                }
            },
            List: {
                type: CategoryList,
                x: MENU_WIDTH,
                alpha: isGridView ? 0 : 1
            },
            GridList: {
                type: MediaList,
                alpha: isGridView ? 1 : 0,
                y: theme.banner.padding.y,
                x: theme.layout.content.x + MENU_WIDTH,
                w: theme.layout.w - (theme.layout.content.x + MENU_WIDTH),
                h: theme.layout.h - 40,
                showCategoryName: false,
                isFullScreen: true,
                navigateOnSelect: true
            }
        };
    }

    set data(value: CategoryModel[] | MediaModel[]) {
        if (this._isGridView) {
            // Grid view: value is a flat array of MediaModel[]
            const items = value as MediaModel[];
            const filtered = items.filter((item) => item?.id);

            if (filtered.length === 0) {
                this.List.patch({
                    alpha: 0
                });
                this.GridList.patch({
                    alpha: 0
                });
                this.Empty.patch({
                    alpha: 1
                });
            } else {
                this.Empty.patch({
                    alpha: 0
                });
                this.List.patch({
                    alpha: 0
                });
                this.GridList.patch({
                    alpha: 1
                    // items: filtered
                });
                if (this.GridList.items.length !== filtered.length) {
                    this.GridList.resetItems(filtered);
                }
                // else {
                //     this.GridList.reloadMovies(filtered);
                // }
            }
        } else {
            // Row view: value is CategoryModel[]
            const categories = value as CategoryModel[];
            const filtered = categories.filter((item) => item.items && item.items.length && item.items[0]?.id);

            if (filtered.length === 0) {
                this.List.patch({
                    alpha: 0
                });
                this.GridList.patch({
                    alpha: 0
                });
                this.Empty.patch({
                    alpha: 1
                });
            } else {
                this.Empty.patch({
                    alpha: 0
                });
                this.GridList.patch({
                    alpha: 0
                });
                this.List.patch({
                    alpha: 1
                });
                if (this.List.items.length !== filtered.length) {
                    this.List.reload(filtered);
                } else {
                    this.List.reloadMovies(filtered);
                }
            }
        }
    }

    override _getFocused() {
        return this._isGridView ? this.GridList : this.List;
    }

    override _firstActive() {
        this.application.emit("clearBackground");
        this.application.emit("setActivePage", PAGES.favorite.name);
    }

    override _handleBack() {
        if (!Router.getHistory().length) return true;
        Router.go(Router.getHistory().length * -1);
    }

    override _inactive() {
        this.stage.gc();
    }
}
